// Typed API surface for the purchasing & intake feature (plan.md G20). Every hook
// is built from the two shared primitives (src/hooks/useApi.ts) over the typed
// fetch wrapper (src/api/client.ts) — no direct `fetch`/`Response` handling here,
// matching the pattern G18 established. Row/response shapes mirror the wire
// contracts in services/api/src/modules/{purchasing,intake,config}/* and
// packages/core/src/schemas/{purchasing,intake}.ts; request bodies are typed
// directly against `@zebra/core`'s zod-inferred input types so a schema change
// there surfaces here as a compile error instead of a silent runtime mismatch.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../../api/client';
import { useApiMutation, useApiQuery } from '../../hooks/useApi';
import type {
  BaseUom,
  GoodsReceiptPostResponse,
  GoodsReceiptResponse,
  GoodsReceiptReverseResponse,
  GoodsReceiptStatus,
  GoodsReceiptWrite,
  LocationType,
  McSource,
  PricingBasis,
  ProductType,
  PurchaseOrderCreateInput,
  PurchaseOrderLineInput,
  PurchaseOrderStatus,
  PurchaseOrderUpdateInput,
  SupplierContractCreateInput,
  SupplierContractUpdateInput,
  SupplierCreateInput,
  SupplierUpdateInput,
  TicketCreateInput,
  TicketDirection,
  TicketResponse,
  TicketStatus,
  VatTreatment,
} from '@zebra/core';

export interface Page<T> {
  items: T[];
  nextCursor: string | null;
}

// ---------------------------------------------------------------------------
// Config lookups (config module, G13) — read-only pickers used throughout this
// feature. Fetched with a generous limit rather than real search UI: v1 tenants
// are expected to have small masters (products/locations/sites/cost-centres in
// the tens, not thousands).
// ---------------------------------------------------------------------------

export interface SiteRow {
  id: string;
  code: string;
  name: string;
}

export interface LocationRow {
  id: string;
  siteId: string;
  code: string;
  name: string;
  type: LocationType;
  parentId: string | null;
  isActive: boolean;
}

export interface ProductRow {
  id: string;
  sku: string;
  name: string;
  type: ProductType;
  baseUom: BaseUom;
  isMoistureTracked: boolean;
  defaultMcPct: string | null;
  vatTreatment: VatTreatment;
  minLevel: string | null;
  reorderQty: string | null;
  isActive: boolean;
}

export interface CostCentreRow {
  id: string;
  siteId: string;
  code: string;
  name: string;
}

export function useSites() {
  return useApiQuery<Page<SiteRow>>(['config', 'sites'], '/config/sites', { query: { limit: 200 } });
}

export function useLocations(params: { siteId?: string; isActive?: boolean } = {}) {
  return useApiQuery<Page<LocationRow>>(['config', 'locations', params], '/config/locations', {
    query: { siteId: params.siteId, isActive: params.isActive, limit: 200 },
  });
}

export function useProducts(params: { isActive?: boolean; type?: ProductType } = {}) {
  return useApiQuery<Page<ProductRow>>(['config', 'products', params], '/config/products', {
    query: { isActive: params.isActive, type: params.type, limit: 200 },
  });
}

export function useCostCentres(params: { siteId?: string } = {}) {
  return useApiQuery<Page<CostCentreRow>>(['config', 'cost-centres', params], '/config/cost-centres', {
    query: { siteId: params.siteId, limit: 200 },
  });
}

// ---------------------------------------------------------------------------
// Suppliers (purchasing module, G6)
// ---------------------------------------------------------------------------

export interface SupplierRow {
  id: string;
  code: string;
  name: string;
  vatNumber: string | null;
  email: string | null;
  phone: string | null;
  paymentTerms: string | null;
  xeroContactId: string | null;
  isActive: boolean;
}

export function useSuppliers(params: { isActive?: boolean } = {}) {
  return useApiQuery<Page<SupplierRow>>(['purchasing', 'suppliers', params], '/purchasing/suppliers', {
    query: { isActive: params.isActive, limit: 200 },
  });
}

export function useCreateSupplier() {
  const qc = useQueryClient();
  return useApiMutation<SupplierRow, SupplierCreateInput>('/purchasing/suppliers', 'POST', {
    onSuccess: () => qc.invalidateQueries({ queryKey: ['purchasing', 'suppliers'] }),
  });
}

export function useUpdateSupplier(id: string) {
  const qc = useQueryClient();
  return useApiMutation<SupplierRow, SupplierUpdateInput>(`/purchasing/suppliers/${id}`, 'PATCH', {
    onSuccess: () => qc.invalidateQueries({ queryKey: ['purchasing', 'suppliers'] }),
  });
}

// ---------------------------------------------------------------------------
// Supplier contracts (purchasing module, G6) — sole web owner (plan.md QC fix #1)
// ---------------------------------------------------------------------------

export interface WireMoistureBand {
  mcMin: number;
  mcMax: number;
  priceCents: string;
}

export interface SupplierContractRow {
  id: string;
  supplierId: string;
  productId: string;
  pricingBasis: PricingBasis;
  priceCentsPerTonne: string | null;
  bands: WireMoistureBand[] | null;
  validFrom: string;
  validTo: string | null;
  isActive: boolean;
}

export function useSupplierContracts(params: { supplierId?: string; productId?: string; isActive?: boolean } = {}) {
  return useApiQuery<Page<SupplierContractRow>>(
    ['purchasing', 'supplier-contracts', params],
    '/purchasing/supplier-contracts',
    { query: { supplierId: params.supplierId, productId: params.productId, isActive: params.isActive, limit: 200 } },
  );
}

// `@zebra/core`'s money-cents fields (`priceCentsPerTonne`, `bands[].priceCents`)
// are typed `bigint` on the zod-INFERRED input types (moneyCentsSchema accepts a
// bigint OR a numeric string but its `.transform` normalizes to bigint, and
// `z.infer` reflects that OUTPUT type). A browser can never actually SEND a raw
// bigint as a request body — `JSON.stringify` throws on one — so every write
// hook that touches a money-cents field uses a local "wire" variant of the
// input type with those fields typed as the numeric-string form instead. The
// server's zod validator accepts that string form directly (the same union
// moneyCentsSchema always allowed), so this is not a shape change, only a
// TypeScript-side correction for what a fetch body can actually carry.
export type SupplierContractWriteInput = Omit<SupplierContractCreateInput, 'priceCentsPerTonne' | 'bands'> & {
  priceCentsPerTonne?: string | null;
  bands?: WireMoistureBand[] | null;
};
export type SupplierContractUpdateWriteInput = Omit<SupplierContractUpdateInput, 'priceCentsPerTonne' | 'bands'> & {
  priceCentsPerTonne?: string | null;
  bands?: WireMoistureBand[] | null;
};

export function useCreateSupplierContract() {
  const qc = useQueryClient();
  return useApiMutation<SupplierContractRow, SupplierContractWriteInput>('/purchasing/supplier-contracts', 'POST', {
    onSuccess: () => qc.invalidateQueries({ queryKey: ['purchasing', 'supplier-contracts'] }),
  });
}

export function useUpdateSupplierContract(id: string) {
  const qc = useQueryClient();
  return useApiMutation<SupplierContractRow, SupplierContractUpdateWriteInput>(`/purchasing/supplier-contracts/${id}`, 'PATCH', {
    onSuccess: () => qc.invalidateQueries({ queryKey: ['purchasing', 'supplier-contracts'] }),
  });
}

// ---------------------------------------------------------------------------
// Purchase orders (+ lines) (purchasing module, G6)
// ---------------------------------------------------------------------------

export interface PurchaseOrderRow {
  id: string;
  siteId: string;
  number: string;
  supplierId: string;
  status: PurchaseOrderStatus;
  expectedAt: string | null;
  deliveryLocationId: string | null;
  notes: string | null;
  currency: string;
}

/**
 * A PO line is EITHER mass-priced (`qtyTonnes`+`pricingBasis`, tonnes/pricing-
 * basis fields non-null, `qty`/`priceCentsPerUnit` null) OR EACH-priced
 * (`qty`+`priceCentsPerUnit` non-null, the mass fields null) — mirrors
 * `services/api`'s `serializePoLine` (a passthrough of every
 * `purchase_order_lines` column, `.strict()`-guarded at the DB layer by the
 * `_quantity_mode` CHECK). Previously this claimed `qtyTonnes`/`pricingBasis`
 * non-null and had no `qty`/`priceCentsPerUnit` at all — dishonest vs the wire
 * for an EACH line (fix round G-WebA, 2026-07-07). Discriminate with
 * `isCountLine`/`isMassLine` (`@zebra/core`), never a local null check.
 */
export interface PurchaseOrderLineRow {
  id: string;
  poId: string;
  lineNo: number;
  productId: string;
  qtyTonnes: string | null;
  pricingBasis: PricingBasis | null;
  priceCentsPerTonne: string | null;
  bands: WireMoistureBand[] | null;
  qty: string | null;
  priceCentsPerUnit: string | null;
  vatTreatment: VatTreatment;
  costCentreId: string | null;
  receivedQtyKg: string;
  receivedQty: string;
}

export interface PurchaseOrderDetail extends PurchaseOrderRow {
  lines: PurchaseOrderLineRow[];
}

export function usePurchaseOrders(params: { status?: PurchaseOrderStatus; supplierId?: string } = {}) {
  return useApiQuery<Page<PurchaseOrderRow>>(['purchasing', 'purchase-orders', params], '/purchasing/purchase-orders', {
    query: { status: params.status, supplierId: params.supplierId, limit: 200 },
  });
}

export function usePurchaseOrder(id: string | undefined) {
  return useApiQuery<PurchaseOrderDetail>(
    ['purchasing', 'purchase-order', id],
    `/purchasing/purchase-orders/${id}`,
    { enabled: Boolean(id) },
  );
}

/** Wire-safe variant of `PurchaseOrderLineInput` — see the comment above
 * `SupplierContractWriteInput` for why: a PO line's `priceCentsPerTonne`/
 * `bands[].priceCents` are typed `bigint` on the zod-inferred type, but a
 * fetch body can only carry the numeric-string form. */
export type PurchaseOrderLineWriteInput = Omit<PurchaseOrderLineInput, 'priceCentsPerTonne' | 'bands'> & {
  priceCentsPerTonne?: string | null;
  bands?: WireMoistureBand[] | null;
};
export type PurchaseOrderWriteInput = Omit<PurchaseOrderCreateInput, 'lines'> & { lines: PurchaseOrderLineWriteInput[] };
export type PurchaseOrderUpdateWriteInput = Omit<PurchaseOrderUpdateInput, 'lines'> & { lines?: PurchaseOrderLineWriteInput[] };

export function useCreatePurchaseOrder() {
  const qc = useQueryClient();
  return useApiMutation<PurchaseOrderDetail, PurchaseOrderWriteInput>('/purchasing/purchase-orders', 'POST', {
    onSuccess: () => qc.invalidateQueries({ queryKey: ['purchasing', 'purchase-orders'] }),
  });
}

export function useUpdatePurchaseOrder(id: string) {
  const qc = useQueryClient();
  return useApiMutation<PurchaseOrderDetail, PurchaseOrderUpdateWriteInput>(`/purchasing/purchase-orders/${id}`, 'PATCH', {
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['purchasing', 'purchase-orders'] });
      qc.invalidateQueries({ queryKey: ['purchasing', 'purchase-order', id] });
    },
  });
}

export function useSendPurchaseOrder(id: string) {
  const qc = useQueryClient();
  return useApiMutation<PurchaseOrderRow, void>(`/purchasing/purchase-orders/${id}/send`, 'POST', {
    onSuccess: () => qc.invalidateQueries({ queryKey: ['purchasing', 'purchase-orders'] }),
  });
}

export function useCancelPurchaseOrder(id: string) {
  const qc = useQueryClient();
  return useApiMutation<PurchaseOrderRow, void>(`/purchasing/purchase-orders/${id}/cancel`, 'POST', {
    onSuccess: () => qc.invalidateQueries({ queryKey: ['purchasing', 'purchase-orders'] }),
  });
}

// ---------------------------------------------------------------------------
// Weighbridge tickets (intake module, G14)
// ---------------------------------------------------------------------------

export function useTickets(params: { status?: TicketStatus; direction?: TicketDirection; purchaseOrderId?: string } = {}) {
  return useApiQuery<Page<TicketResponse>>(['intake', 'weighbridge-tickets', params], '/intake/weighbridge-tickets', {
    query: { status: params.status, direction: params.direction, purchaseOrderId: params.purchaseOrderId, limit: 200 },
  });
}

export function useCreateTicket() {
  return useApiMutation<TicketResponse, TicketCreateInput>('/intake/weighbridge-tickets', 'POST');
}

export function usePostTicket(id: string) {
  const qc = useQueryClient();
  return useApiMutation<TicketResponse, void>(`/intake/weighbridge-tickets/${id}/post`, 'POST', {
    onSuccess: () => qc.invalidateQueries({ queryKey: ['intake', 'weighbridge-tickets'] }),
  });
}

export function useReverseTicket(id: string) {
  const qc = useQueryClient();
  return useApiMutation<TicketResponse, void>(`/intake/weighbridge-tickets/${id}/reverse`, 'POST', {
    onSuccess: () => qc.invalidateQueries({ queryKey: ['intake', 'weighbridge-tickets'] }),
  });
}

// ---------------------------------------------------------------------------
// Goods receipts (intake module, G14)
// ---------------------------------------------------------------------------

export function useGoodsReceipts(params: { status?: GoodsReceiptStatus; poId?: string } = {}) {
  return useApiQuery<Page<GoodsReceiptResponse>>(['intake', 'goods-receipts', params], '/intake/goods-receipts', {
    query: { status: params.status, poId: params.poId, limit: 200 },
  });
}

export function useGoodsReceipt(id: string | undefined) {
  return useApiQuery<GoodsReceiptResponse>(['intake', 'goods-receipt', id], `/intake/goods-receipts/${id}`, {
    enabled: Boolean(id),
  });
}

export function useCreateGoodsReceipt() {
  const qc = useQueryClient();
  return useApiMutation<GoodsReceiptResponse, GoodsReceiptWrite>('/intake/goods-receipts', 'POST', {
    onSuccess: () => qc.invalidateQueries({ queryKey: ['intake', 'goods-receipts'] }),
  });
}

export function useUpdateGoodsReceipt(id: string) {
  const qc = useQueryClient();
  return useApiMutation<GoodsReceiptResponse, GoodsReceiptWrite>(`/intake/goods-receipts/${id}`, 'PATCH', {
    onSuccess: () => qc.invalidateQueries({ queryKey: ['intake', 'goods-receipts'] }),
  });
}

export function usePostGoodsReceipt(id: string) {
  const qc = useQueryClient();
  return useApiMutation<GoodsReceiptPostResponse, void>(`/intake/goods-receipts/${id}/post`, 'POST', {
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['intake', 'goods-receipts'] });
      qc.invalidateQueries({ queryKey: ['purchasing', 'purchase-orders'] });
      qc.invalidateQueries({ queryKey: ['reporting', 'reorder'] });
    },
  });
}

export function useReverseGoodsReceipt(id: string) {
  const qc = useQueryClient();
  return useApiMutation<GoodsReceiptReverseResponse, void>(`/intake/goods-receipts/${id}/reverse`, 'POST', {
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['intake', 'goods-receipts'] });
      qc.invalidateQueries({ queryKey: ['purchasing', 'purchase-orders'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Reorder report (reporting module, G19) — read-only; used by the Reorder tab.
// ---------------------------------------------------------------------------

export interface ReorderItem {
  productId: string;
  productSku: string;
  productName: string;
  onHandQty: string;
  minLevel: string;
  suggestedQty: string | null;
  preferredSupplierId: string | null;
  preferredSupplierName: string | null;
}

export function useReorderSuggestions(params: { siteId?: string } = {}) {
  return useApiQuery<{ items: ReorderItem[] }>(['reporting', 'reorder', params], '/reporting/reorder', {
    query: { siteId: params.siteId },
  });
}

// ---------------------------------------------------------------------------
// "Receive a load" (§8.1) — a composite action spanning three dedicated posting
// endpoints (create ticket -> create goods receipt -> post goods receipt), which
// the server ties together as one user action (goods-receipts.ts's `/post`
// transitions a linked DRAFT ticket to POSTED in the SAME db transaction). This
// is intentionally NOT a single `useApiMutation` (that helper is for one fixed
// path) — it is the one place in this feature that calls `apiFetch` directly,
// composing three calls into a single mutation so the wizard gets one
// isPending/error/data lifecycle for the whole flow.
// ---------------------------------------------------------------------------

export interface ReceiveLoadInput {
  siteId: string;
  poId: string;
  poLineId: string;
  productId: string;
  vehicleReg: string;
  externalTicketNo?: string | null;
  haulier?: string | null;
  driver?: string | null;
  grossKg: string;
  tareKg: string;
  weighedAt: string;
  mcPct?: string | null;
  mcSource?: McSource | null;
  locationId: string;
  receivedAt: string;
  notes?: string | null;
}

/**
 * Thrown when the LAST of the three composite calls (`/goods-receipts/:id/post`)
 * fails — the ticket and goods receipt from the first two calls already exist
 * (as DRAFT) at that point, so this carries the created `grId` alongside the
 * original failure (e.g. an `ApiRequestError` with `code ===
 * 'MOISTURE_OUT_OF_BAND'`) so the caller can recover WITHOUT re-running the
 * whole composite action (which would create a SECOND ticket+GR, duplicating
 * the first). ReceiveLoadWizard uses this to hand an ADMIN straight to
 * `GoodsReceiptDraftModal` (grId) for the moisture-band override control
 * rather than leaving them to hunt down the stalled draft in the Receipts tab.
 */
export class ReceiveLoadPostError extends Error {
  readonly grId: string;
  readonly originalError: unknown;

  constructor(grId: string, originalError: unknown) {
    super(originalError instanceof Error ? originalError.message : 'Failed to post the goods receipt');
    this.name = 'ReceiveLoadPostError';
    this.grId = grId;
    this.originalError = originalError;
  }
}

export function useReceiveLoad() {
  const qc = useQueryClient();
  return useMutation<GoodsReceiptPostResponse, Error, ReceiveLoadInput>({
    mutationFn: async (input) => {
      const ticketBody: TicketCreateInput = {
        siteId: input.siteId,
        direction: 'IN',
        externalTicketNo: input.externalTicketNo ?? null,
        vehicleReg: input.vehicleReg,
        haulier: input.haulier ?? null,
        driver: input.driver ?? null,
        grossKg: input.grossKg,
        tareKg: input.tareKg,
        mcPct: input.mcPct ?? null,
        mcSource: input.mcSource ?? null,
        weighedAt: new Date(input.weighedAt),
        purchaseOrderId: input.poId,
        salesOrderId: null,
        customerId: null,
        productId: input.productId,
        notes: input.notes ?? null,
      };
      const ticket = await apiFetch<TicketResponse>('/intake/weighbridge-tickets', { method: 'POST', body: ticketBody });

      const grBody: GoodsReceiptWrite = {
        poId: input.poId,
        ticketId: ticket.id,
        locationId: input.locationId,
        receivedAt: new Date(input.receivedAt),
        notes: input.notes ?? null,
        lines: [{ poLineId: input.poLineId, productId: input.productId, qtyKg: ticket.netKg, mcPct: input.mcPct ?? null }],
      };
      const gr = await apiFetch<GoodsReceiptResponse>('/intake/goods-receipts', { method: 'POST', body: grBody });

      try {
        return await apiFetch<GoodsReceiptPostResponse>(`/intake/goods-receipts/${gr.id}/post`, { method: 'POST' });
      } catch (err) {
        throw new ReceiveLoadPostError(gr.id, err);
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['purchasing', 'purchase-orders'] });
      qc.invalidateQueries({ queryKey: ['purchasing', 'purchase-order'] });
      qc.invalidateQueries({ queryKey: ['intake'] });
      qc.invalidateQueries({ queryKey: ['reporting', 'reorder'] });
    },
  });
}
